import { useMemo } from 'react';
import JsonFormEditor from './JsonFormEditor';
import Loading from '@/components/elements/Loading';
import PageTitle from '@/components/layout/PageTitle';
import NotFound from '@/components/pages/NotFound';
import useSafeParams from '@/hooks/useSafeParams';
import ApolloErrorAlert from '@/modules/errors/components/ApolloErrorAlert';
import ErrorAlert from '@/modules/errors/components/ErrorAlert';
import {
  useGetFormDefinitionFieldsForJsonEditorQuery,
  useUpdateFormDefinitionFromJsonEditorMutation,
} from '@/types/gqlTypes';

const JsonFormEditorPage = () => {
  const { formId } = useSafeParams() as { formId: string };

  const {
    data: { formDefinition } = {},
    loading: fetchLoading,
    error,
  } = useGetFormDefinitionFieldsForJsonEditorQuery({
    variables: { id: formId },
    fetchPolicy: 'network-only', // always get the latest form
  });

  const [
    updateFormDefinition,
    { loading, data: saveResponse, error: saveError },
  ] = useUpdateFormDefinitionFromJsonEditorMutation();

  const validationErrors = useMemo(
    () => saveResponse?.updateFormDefinition?.errors,
    [saveResponse]
  );
  if (error) throw error;
  if (!formDefinition && fetchLoading) return <Loading />;
  if (!formDefinition) return <NotFound />;

  return (
    <>
      <PageTitle title={`Edit Form: ${formDefinition.title}`} />
      {saveError && <ApolloErrorAlert error={saveError} />}
      {validationErrors && <ErrorAlert errors={validationErrors} />}
      <JsonFormEditor
        definition={formDefinition.rawDefinition}
        role={formDefinition.role}
        onSave={(values) => {
          updateFormDefinition({
            variables: {
              id: formDefinition.id,
              input: { definition: JSON.stringify(values) },
            },
          });
        }}
        saveLoading={loading}
      />
    </>
  );
};

export default JsonFormEditorPage;
